import React from 'react';
import { History } from 'lucide-react';
import { InventoryItem, SupplierMap } from '../types';

interface RecentTransaction {
  id: string;
  ean: string;
  quantity_change: number;
  price: number;
  selling_price: number;
  product_name: string;
  transaction_type: string;
  created_at: string;
}

interface TransactionHistoryProps {
  recentTransactions: RecentTransaction[];
  recentScans: InventoryItem[];
  supplierData: SupplierMap;
}

export const TransactionHistory: React.FC<TransactionHistoryProps> = ({
  recentTransactions,
  recentScans,
  supplierData,
}) => {
  return (
    <div className="bg-card rounded-xl shadow-sm border border-slate-100 dark:border-gray-700">
      <div className="px-4 md:px-6 py-3 border-b border-slate-100 dark:border-gray-700 bg-slate-50/50 dark:bg-gray-900/50 flex items-center gap-2">
        <History size={16} className="text-slate-400 dark:text-gray-500" />
        <h3 className="font-semibold text-slate-600 dark:text-gray-300 text-sm">
          Legutóbbi 5 tétel
        </h3>
      </div>
      <div className="divide-y divide-slate-50 dark:divide-gray-700">
        {recentTransactions && recentTransactions.length > 0 ? (
          recentTransactions.map((transaction) => {
            if (!transaction) return null;
            const isReturn = transaction.transaction_type === 'RETURN';
            const isSale = transaction.transaction_type === 'SALE';

            // CRITICAL FIX: RETURN transactions ALWAYS increase stock = ALWAYS positive (green)
            // Force positive display for RETURN regardless of data source
            let quantityValue: number;
            let isPositive: boolean;

            if (isReturn) {
              // RETURN = Stock INCREASES = ALWAYS positive (force it)
              quantityValue = Math.abs(transaction.quantity_change);
              // Ensure it's never zero or negative
              if (quantityValue <= 0) quantityValue = 1;
              isPositive = true; // FORCE positive for RETURN
            } else if (isSale) {
              // SALE = Stock DECREASES = ALWAYS negative (red)
              quantityValue = Math.abs(transaction.quantity_change);
              isPositive = false; // FORCE negative for SALE
            } else {
              // COUNT, SCRAP, INTERNAL: Use actual sign from data
              quantityValue = Math.abs(transaction.quantity_change);
              isPositive = transaction.quantity_change > 0;
            }

            const unitPrice =
              isSale && transaction.selling_price > 0
                ? transaction.selling_price
                : transaction.price;
            const totalValue = Math.abs(quantityValue * unitPrice);

            // CRITICAL: Explicitly show "+" for RETURN, "-" for SALE
            const quantityDisplay = isReturn
              ? `+ ${quantityValue} db` // RETURN = Always positive
              : isSale
                ? `- ${quantityValue} db` // SALE = Always negative
                : isPositive
                  ? `+ ${quantityValue} db` // Other positive
                  : `- ${quantityValue} db`; // Other negative

            return (
              <div
                key={transaction.id}
                className="px-4 md:px-6 py-3 flex justify-between items-center text-sm gap-3"
              >
                <div className="min-w-0 flex-1 truncate pr-2">
                  <span className="font-medium text-gray-900 dark:text-white block truncate">
                    {transaction.product_name || (
                      <span className="text-amber-600 dark:text-amber-400">Ismeretlen</span>
                    )}
                  </span>
                  <span className="text-xs text-gray-500 dark:text-gray-400 block font-mono truncate">
                    {transaction.ean}
                  </span>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span
                    className={`font-bold px-2 py-1 rounded ${
                      isReturn
                        ? 'text-purple-600 dark:text-purple-400 bg-purple-100 dark:bg-purple-900/30'
                        : isPositive
                          ? 'text-green-600 dark:text-green-400 bg-green-100 dark:bg-green-900/30'
                          : 'text-red-600 dark:text-red-400 bg-red-100 dark:bg-red-900/30'
                    }`}
                  >
                    {quantityDisplay}
                  </span>
                  {totalValue > 0 && (
                    <span
                      className={`font-semibold ${
                        isReturn
                          ? 'text-purple-600 dark:text-purple-400'
                          : isPositive
                            ? 'text-green-600 dark:text-green-400'
                            : 'text-red-600 dark:text-red-400'
                      }`}
                    >
                      {totalValue.toLocaleString('hu-HU')} Ft
                    </span>
                  )}
                </div>
              </div>
            );
          })
        ) : recentScans && recentScans.length > 0 ? (
          recentScans
            .map((item) => {
              if (!item?.ean) return null;
              return (
                <div
                  key={item.ean}
                  className="px-4 md:px-6 py-3 flex justify-between items-center text-sm gap-3"
                >
                  <div className="min-w-0 flex-1 truncate pr-2">
                    <span className="font-medium text-gray-900 dark:text-white block truncate">
                      {supplierData[item.ean]?.name || (
                        <span className="text-amber-600 dark:text-amber-400">Ismeretlen</span>
                      )}
                    </span>
                    <span className="text-xs text-gray-500 dark:text-gray-400 block font-mono truncate">
                      {item.ean}
                    </span>
                  </div>
                  <span className="font-bold text-gray-900 dark:text-white bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded shrink-0">
                    {item.quantity ?? 0} db
                  </span>
                </div>
              );
            })
            .filter(Boolean)
        ) : (
          <div className="p-6 text-center text-slate-400 dark:text-gray-500 text-xs italic">
            Még nincs rögzített adat.
          </div>
        )}
      </div>
    </div>
  );
};
